import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import Brand from "@/components/Brand";
import { CheckCircle, XCircle, WarningOctagon, ArrowLeft, IdentificationCard } from "@phosphor-icons/react";

// Public read-only citation view. Scanning the QR on a printed citation PDF
// lands here — judges, lawyers, and owners can confirm the citation exists,
// its status, and its full contents without needing an account.
export default function CitationVerify() {
    const { id } = useParams();
    const { t } = useI18n();
    const [v, setV] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/public/citations/${id}`)
            .then((r) => setV(r.data))
            .catch((e) => setError(e?.response?.data?.detail || t("error")));
    }, [id]); // eslint-disable-line

    const statusIcon = v?.status === "paid" ? <CheckCircle size={20} weight="fill" className="text-green-700" />
        : v?.status === "dismissed" ? <XCircle size={20} weight="fill" className="text-muted-foreground" />
        : <WarningOctagon size={20} weight="fill" className="text-primary" />;

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="px-6 py-4 border-b border-border flex items-center justify-between">
                <Brand />
                <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft size={12} weight="bold" />{t("home") || "Home"}</Link>
            </header>
            <div className="flex-1 max-w-2xl mx-auto w-full p-6" data-testid="citation-verify-page">
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· PUBLIC · CITATION VERIFY</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("citationVerifyTitle")}</h1>
                <p className="text-sm text-muted-foreground mt-1 mb-6">{t("citationVerifyHint")}</p>

                {error && (
                    <Card className="p-6 rounded-sm border-primary bg-primary/5 text-primary text-sm flex items-center gap-2" data-testid="citation-not-found">
                        <XCircle size={18} weight="fill" />{error}
                    </Card>
                )}
                {v && (
                    <Card className="p-6 rounded-sm border-border space-y-4">
                        <div className="flex items-center gap-3 flex-wrap">
                            <IdentificationCard size={28} weight="duotone" className="text-primary" />
                            <div>
                                <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("plateNumber")}</div>
                                <div className="mono text-2xl font-black" data-testid="cv-plate">{v.plate_number}</div>
                            </div>
                            <div className="ms-auto flex items-center gap-2" data-testid="cv-status">
                                {statusIcon}
                                <span className={`text-[10px] uppercase tracking-widest font-bold ${v.status === "paid" ? "text-green-700" : v.status === "dismissed" ? "text-muted-foreground" : "text-primary"}`}>{v.status}</span>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3 text-sm">
                            <Field label={t("ownerName")} value={v.owner_name} />
                            <Field label={t("violationType")} value={v.type_label} />
                            <Field label={t("fineAmount")} value={`${v.amount} ${v.currency}`} mono />
                            <Field label={t("location")} value={v.location} />
                            <Field label={t("auditActor")} value={v.officer_email} mono />
                            <Field label={t("auditTimestamp")} value={new Date(v.issued_at).toLocaleString()} mono />
                            <Field label="ID" value={v.id} mono />
                            <Field label={t("updated") || "Updated"} value={new Date(v.updated_at).toLocaleString()} mono />
                        </div>
                        {v.notes && (
                            <div>
                                <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">{t("notes")}</div>
                                <div className="text-sm bg-secondary/40 p-3 rounded-sm mt-1">{v.notes}</div>
                            </div>
                        )}

                        {v.citation_jws && (
                            <div className="border-t border-border pt-4" data-testid="cv-signature">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">{t("citationSignatureTitle")}</div>
                                    {v.signature_valid === true && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-green-100 text-green-900 border border-green-400 text-[10px] uppercase tracking-widest font-bold" data-testid="cv-sig-valid">
                                            <CheckCircle size={11} weight="fill" />{t("citationSignatureValid")}
                                        </span>
                                    )}
                                    {v.signature_valid === false && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-bold" data-testid="cv-sig-invalid">
                                            <XCircle size={11} weight="fill" />{t("citationSignatureInvalid")}
                                        </span>
                                    )}
                                </div>
                                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                                    <Field label={t("citationSignatureAlgo")} value={v.signature_algorithm} mono />
                                    <Field label="KID" value={v.kid} mono />
                                </div>
                                <div className="mt-3">
                                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground">JWS</div>
                                    <div className="mono text-[10px] break-all bg-secondary/40 p-2 rounded-sm mt-1 max-h-24 overflow-y-auto" data-testid="cv-jws">{v.citation_jws}</div>
                                    <button
                                        type="button"
                                        className="mt-1 text-[10px] tracking-widest uppercase text-primary hover:underline"
                                        onClick={() => { navigator.clipboard.writeText(v.citation_jws); }}
                                        data-testid="cv-copy-jws"
                                    >{t("copy") || "Copy"}</button>
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-2">{t("citationSignatureHelp")}</div>
                            </div>
                        )}

                        <div className="text-[11px] text-muted-foreground border-t border-border pt-3">{t("citationVerifyFooter")}</div>
                        <Link to="/owner-portal" className="inline-block text-xs uppercase tracking-widest text-primary hover:underline">{t("citationVerifyPayCta")}</Link>
                    </Card>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{label}</div>
            <div className={mono ? "mono" : ""}>{value || <span className="text-muted-foreground">—</span>}</div>
        </div>
    );
}
